/** The teacher's bundle list. Everything about one bundle is in its editor. */

import { useState } from 'react';
import type { BundleCardWire } from '@wire';
import { teach } from '../api';
import { AssignDialog } from '../components/AssignDialog';
import { Menu, StateBadge } from '../components/Bits';
import { Empty, ErrorNote, Loading, OkNote, PageTitle, Panel } from '../components/Chrome';
import { NewBundleDialog } from '../components/NewBundleDialog';
import { ds } from '../ds';
import { Link, useRouter } from '../router';
import { HAIR, meta, monoPlain, plural, safeColour } from '../theme';
import { useActions, useLoad } from '../useLoad';

export default function TeachBundles() {
  const { Button, Dialog } = ds();
  const { navigate } = useRouter();
  const { data, error, loading, reload } = useLoad(
    () => teach.bundles(),
    'Could not load your bundles.'
  );
  const { busy, error: wrote, ok, act, setOk } = useActions(reload);

  const [creating, setCreating] = useState(false);
  const [assign, setAssign] = useState<BundleCardWire | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<BundleCardWire | null>(null);

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!data) return loading ? <Loading /> : null;

  return (
    <>
      <PageTitle title="Bundles." meta={plural(data.bundles.length, 'bundle')}>
        <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
          New bundle
        </Button>
      </PageTitle>

      <ErrorNote>{wrote}</ErrorNote>
      <OkNote>{ok}</OkNote>

      {data.bundles.length === 0 ? (
        <Empty line="No bundle yet.">
          <Button variant="secondary" size="sm" onClick={() => setCreating(true)}>
            New bundle
          </Button>
        </Empty>
      ) : (
        <Panel>
          {data.bundles.map((bundle) => (
            <div
              key={bundle.id}
              className="mv-row"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '14px 4px',
                borderTop: HAIR,
                flexWrap: 'wrap'
              }}
            >
              <span
                aria-hidden="true"
                style={{ width: 30, height: 30, borderRadius: 9, background: safeColour(bundle.colour) }}
              />

              <div style={{ flex: 1, minWidth: 160, display: 'grid', gap: 3 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Link to={`/teach/bundles/${bundle.id}`}>
                    <span style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-strong)' }}>
                      {bundle.title}
                    </span>
                  </Link>
                  {bundle.state !== 'live' && <StateBadge state={bundle.state} />}
                </span>
                <span style={monoPlain}>
                  {meta(plural(bundle.courseCount, 'course'), bundle.ordered ? 'in order' : null)}
                </span>
              </div>

              <Button variant="secondary" size="sm" onClick={() => setAssign(bundle)}>
                Assign
              </Button>

              <Menu
                label={`More for ${bundle.title}`}
                items={[
                  { label: 'Edit', onSelect: () => navigate(`/teach/bundles/${bundle.id}`) },
                  bundle.state === 'live'
                    ? {
                        label: 'Unpublish',
                        onSelect: () =>
                          void act(
                            () => teach.updateBundle(bundle.id, { state: 'draft' }),
                            `${bundle.title} is no longer live.`
                          )
                      }
                    : {
                        label: 'Publish',
                        onSelect: () =>
                          void act(
                            () => teach.updateBundle(bundle.id, { state: 'live' }),
                            `${bundle.title} is live.`
                          )
                      },
                  { label: 'Delete', danger: true, onSelect: () => setConfirmDelete(bundle) }
                ]}
              />
            </div>
          ))}
        </Panel>
      )}

      {creating && (
        <NewBundleDialog
          onClose={() => setCreating(false)}
          onCreated={(id) => navigate(`/teach/bundles/${id}`)}
        />
      )}

      {assign && (
        <AssignDialog
          bundleId={assign.id}
          subject={assign.title}
          onClose={() => setAssign(null)}
          onDone={(result) => {
            setAssign(null);
            setOk(
              `Assigned to ${plural(result.assigned, 'person', 'people')}.${
                result.alreadyAssigned > 0 ? ` ${result.alreadyAssigned} already had it.` : ''
              }`
            );
            void reload();
          }}
        />
      )}

      {confirmDelete && (
        <Dialog
          open
          position="fixed"
          width={440}
          title={`Delete ${confirmDelete.title}?`}
          description="The courses in it stay. Its assignments do not."
          onClose={() => setConfirmDelete(null)}
          footer={
            <>
              <Button variant="ghost" size="md" onClick={() => setConfirmDelete(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                size="md"
                disabled={busy}
                onClick={() => {
                  const bundle = confirmDelete;
                  setConfirmDelete(null);
                  void act(() => teach.deleteBundle(bundle.id), `${bundle.title} is deleted.`);
                }}
              >
                Delete
              </Button>
            </>
          }
        />
      )}
    </>
  );
}
